import React, { useRef, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import {
  SendOutlined,
  PaperClipOutlined,
  PlayCircleOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  CloseOutlined,
  SyncOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  EnterOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Typography,
  FloatButton,
  Input,
  message as antdMessage,
  Tag,
  Popconfirm,
  Badge,
  Avatar,
  Tooltip,
  Popover,
} from "antd";
import ArrowLeftCircleIcon from "../../Theme/CustomIcons/Icons/Arrow/ArrowLeftCircleIcon";
import ProfileCircleIcon from "../../Theme/CustomIcons/Icons/Users/ProfileCirlceIcon";
import CloseTicketIcon from "../../Theme/CustomIcons/Icons/CustomIcons/CloseTicket";
import { useDispatch, useSelector } from "react-redux";
import {
  createSupportTicketAction,
  getSupportTicketByIdAction,
  createSupportMessageAction,
  getSupportMesaageByTicketIdAction,
  updateSupportTicketAction,
  getAllSupportTicketAction,
} from "../../store/action/admin/supportTicketActions";
import DocumentTexIcon from "../../Theme/CustomIcons/Icons/ContentEdit/DocumentTextIcon";
import dayjs from "dayjs";
import { LoadingOutlined } from "@ant-design/icons";
import { css } from "@emotion/css";
import { getNotificatonAction } from "../../store/action/admin/notificationsActions";
import DownloadSquareIcon from "../../Theme/CustomIcons/Icons/Arrow/DownloadSquareIcon";

const { TextArea } = Input;

const isOnlyEmojis = (text) => {
  if (!text) return false;
  const clean = text.replace(/<[^>]*>/g, "").trim();
  if (!clean) return false;
  const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}\u{1F191}-\u{1F251}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{1F300}-\u{1F5FF}]/gu;
  const remaining = clean.replace(emojiRegex, "").replace(/\s/g, "");
  return remaining.length === 0;
};

const parseMarkdown = (text) => {
  if (!text) return "";

  // If it's already HTML (e.g. from rich text editor), return as is
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  if (isHtml) return text;

  // Escape HTML first to prevent XSS
  let escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Bold: **text**
  escaped = escaped.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text*
  escaped = escaped.replace(/\*(.*?)\*/g, "<em>$1</em>");

  // Code: `code`
  escaped = escaped.replace(/`(.*?)`/g, '<code style="background: rgba(255,255,255,0.12); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em;">$1</code>');

  // Split lines to handle lists and line breaks
  const lines = escaped.split("\n");
  let inList = false;
  let listType = null; // 'ul' or 'ol'
  const resultLines = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    const bulletMatch = line.match(/^(\s*)([•\*\-])\s+(.*)$/);
    const numberMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);

    if (bulletMatch) {
      if (!inList || listType !== "ul") {
        if (inList) resultLines.push(`</${listType}>`);
        resultLines.push('<ul style="margin: 4px 0; padding-left: 20px; list-style-type: disc;">');
        inList = true;
        listType = "ul";
      }
      resultLines.push(`<li>${bulletMatch[3]}</li>`);
    } else if (numberMatch) {
      if (!inList || listType !== "ol") {
        if (inList) resultLines.push(`</${listType}>`);
        resultLines.push('<ol style="margin: 4px 0; padding-left: 20px; list-style-type: decimal;">');
        inList = true;
        listType = "ol";
      }
      resultLines.push(`<li>${numberMatch[3]}</li>`);
    } else {
      if (inList) {
        resultLines.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      resultLines.push(line);
    }
  });

  if (inList) {
    resultLines.push(`</${listType}>`);
  }

  return resultLines.join("<br />");
};

function getShortFileNameWithExtension(urlOrPath, maxLength = 25) {
  if (!urlOrPath) return "unknown";
  const parts = urlOrPath.split("/");
  const fullName = parts[parts.length - 1]; // get last segment

  const dotIndex = fullName.lastIndexOf(".");
  const name = dotIndex !== -1 ? fullName.slice(0, dotIndex) : fullName;
  const ext = dotIndex !== -1 ? fullName.slice(dotIndex) : "";

  if (fullName.length <= maxLength) return fullName;

  const keepLength = Math.max(maxLength - ext.length - 1, 5); // ensure at least 5 chars
  return name.slice(0, keepLength) + "…" + ext;
}

// Helper function to get file type
function getFileType(fileName) {
  const ext = fileName.split(".").pop().toLowerCase();
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "video";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "docx";
  return "other";
}

/// Helper function to get file icon
function getFileIcon(fileType) {
  switch (fileType) {
    case "video":
      return <PlayCircleOutlined style={{ fontSize: "14px" }} />;
    case "pdf":
      return <FilePdfOutlined style={{ fontSize: "14px" }} />;
    case "docx":
      return <FileWordOutlined style={{ fontSize: "14px" }} />;
    default:
      return <DocumentTexIcon style={{ fontSize: "14px" }} />;
  }
}

// Helper function to get file tag styles
function getFileTagStyle(fileType) {
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "6px",
    fontWeight: "500",
    fontSize: "12px",
    position: "relative",
    overflow: "hidden",
  };

  const typeStyles = {
    video: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)",
      border: "1px solid rgba(139, 92, 246, 0.4)",
      color: "#a855f7",
      boxShadow: "0 2px 8px rgba(139, 92, 246, 0.12)",
    },
    pdf: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)",
      border: "1px solid rgba(239, 68, 68, 0.4)",
      color: "#ef4444",
      boxShadow: "0 2px 8px rgba(239, 68, 68, 0.12)",
    },
    docx: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)",
      border: "1px solid rgba(59, 130, 246, 0.4)",
      color: "#3b82f6",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.12)",
    },
    other: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(212, 163, 115, 0.15) 0%, rgba(212, 163, 115, 0.1) 100%)",
      border: "1px solid rgba(212, 163, 115, 0.4)",
      color: "#d4a373",
      boxShadow: "0 2px 8px rgba(212, 163, 115, 0.12)",
    },
  };

  return typeStyles[fileType] || typeStyles.other;
}

// Helper function to get delete button color
function getDeleteButtonColor(fileType) {
  const colorMap = {
    video: "rgba(139, 92, 246, 0.6)",
    pdf: "rgba(239, 68, 68, 0.6)",
    docx: "rgba(59, 130, 246, 0.6)",
    other: "rgba(212, 163, 115, 0.6)",
  };

  return colorMap[fileType] || colorMap.other;
}

// Attachment Tag Component
const AttachmentTag = ({ file, onRemove, index }) => {
  const fileType = getFileType(file.name);
  const tagStyle = getFileTagStyle(fileType);
  const deleteButtonColor = getDeleteButtonColor(fileType);

  return (
    <div style={tagStyle}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          color: "inherit",
          fontSize: "14px",
        }}
      >
        {getFileIcon(fileType)}
      </span>
      <span
        style={{
          maxWidth: "110px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "12px",
        }}
      >
        {file.name}
      </span>
      <button
        onClick={() => onRemove(index)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "14px",
          height: "14px",
          marginLeft: "4px",
          cursor: "pointer",
          borderRadius: "50%",
          background: deleteButtonColor,
          color: "#fff",
          border: "none",
          padding: 0,
          transition: "all 0.2s ease",
          fontSize: "10px",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "scale(1.15)";
          e.target.style.background = deleteButtonColor;
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "scale(1)";
          e.target.style.background = deleteButtonColor;
        }}
        title="Remove file"
      >
        <CloseOutlined style={{ fontSize: "8px" }} />
      </button>
    </div>
  );
};

const ChatView = ({
  setView,
  token,
  activeCategory,
  isAiResponding,
  showDummyMessage,
  supportMessages,
  setSupportMessages,
  isLoader,
  setIsLoader,
  activeTicketId,
}) => {
  const scrollRef = useRef(null); // Ref for auto-scroll
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);

  const [message, setMessage] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const sendRef = useRef(null);
  useEffect(() => {
    sendRef.current = handleSendMessage;
  }, [handleSendMessage]);

  const insertFormat = (type) => {
    const editor = textAreaRef.current?.getEditor();
    if (!editor) return;

    editor.focus();
    const range = editor.getSelection(true);
    if (!range) return;

    const format = editor.getFormat(range);

    switch (type) {
      case "bold":
        editor.format("bold", !format.bold);
        break;
      case "italic":
        editor.format("italic", !format.italic);
        break;
      case "bullet":
        editor.format("list", format.list === "bullet" ? false : "bullet");
        break;
      case "number":
        editor.format("list", format.list === "ordered" ? false : "ordered");
        break;
      case "newline":
        editor.insertText(range.index, "\n");
        editor.setSelection(range.index + 1);
        break;
      default:
        return;
    }
  };

  const insertEmoji = (emojiChar) => {
    const editor = textAreaRef.current?.getEditor();
    if (!editor) return;

    editor.focus();
    const range = editor.getSelection(true);
    if (!range) return;

    editor.insertText(range.index, emojiChar);
    editor.setSelection(range.index + emojiChar.length);
  };

  const modules = React.useMemo(() => ({
    toolbar: false,
    keyboard: {
      bindings: {
        enter: {
          key: 13,
          shiftKey: false,
          handler: function () {
            if (sendRef.current) {
              sendRef.current();
            }
            return false;
          }
        }
      }
    }
  }), []);

  const getSupportMessageByTicketIdList = useSelector(
    (state) => state.getSupportMessageByTicketIdList
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [supportMessages, isAiResponding]);

  useEffect(() => {
    if (
      Array.isArray(getSupportMessageByTicketIdList?.info) &&
      getSupportMessageByTicketIdList?.info?.length > 0
    ) {
      setSupportMessages(getSupportMessageByTicketIdList?.info);
    }
  }, [getSupportMessageByTicketIdList?.info]);

  const tokenInfoList = useSelector((state) => state.tokenInfoList);
  const getSupportTicketByIdList = useSelector(
    (state) => state.getSupportTicketByIdList
  );
  const dispatch = useDispatch();

  const handleCloseTicket = async (id) => {
    const formData = new FormData();
    const companyId =
      tokenInfoList?.info?.company?._id ?? tokenInfoList?.info?.companyId?._id;
    formData.append("subject", getSupportTicketByIdList?.info?.data?.subject);
    formData.append("status", "closed");

    await dispatch(updateSupportTicketAction(formData, id, token, companyId));
    dispatch(getAllSupportTicketAction(token, companyId));
    setView("menu");
  };

  async function handleSendMessage() {
    const cleanMessage = message.replace(/<p><br><\/p>/g, "").trim();
    if (!cleanMessage && !attachedFiles?.length > 0) return;
    setIsLoading(true);
    const companyId =
      tokenInfoList?.info?.company?._id ?? tokenInfoList?.info?.companyId?._id;
    const ticketId = getSupportTicketByIdList?.info?.data?._id;

    const formData1 = new FormData();
    formData1.append("body", cleanMessage || "Shared files");
    // Ensure attachedFiles is an array of File objects
    if (Array.isArray(attachedFiles) && attachedFiles.length > 0) {
      attachedFiles.forEach((file) => {
        // Append each file individually to the same key 'attachment'
        formData1.append("attachment", file, file.name);
      });
    }

    formData1.append("supportId", ticketId);
    formData1.append("maxAI", false);

    setMessage("");
    setAttachedFiles([]);

    await dispatch(createSupportMessageAction(formData1, token, companyId));
    await dispatch(
      getSupportMesaageByTicketIdAction(ticketId, token, companyId)
    );
    setIsLoading(false);

    antdMessage.success("Sent!");
  };

  const removeFile = (indexToRemove) => {
    setAttachedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  async function downloadBlobFile(apiUrl, fileName = "support_file") {
    const response = await fetch(apiUrl);
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const allowedExtensions = [
    "docx",
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "xlsx",
    "csv",
  ];

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    let newFiles = [];
    let totalSize = attachedFiles.reduce((acc, file) => acc + file.size, 0);

    for (let file of files) {
      const extension = file.name.split(".").pop().toLowerCase();

      // Extension validation
      if (!allowedExtensions.includes(extension)) {
        antdMessage.error(`${file.name} has unsupported file format`);
        continue;
      }

      // Per file size validation (10MB)
      if (file.size > MAX_FILE_SIZE) {
        antdMessage.error(`${file.name} exceeds 10MB limit`);
        continue;
      }

      // Total size validation (50MB)
      if (totalSize + file.size > MAX_TOTAL_SIZE) {
        antdMessage.error(`Total attachment size cannot exceed 50MB`);
        break;
      }

      newFiles.push(file);
      totalSize += file.size;
    }

    if (newFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...newFiles]);
    }

    e.target.value = null;
  };

  const onRefresh = async () => {
    if (tokenInfoList?.info && token) {
      setIsLoader(true);
      const companyId =
        tokenInfoList?.info?.company?._id ??
        tokenInfoList?.info?.companyId?._id;
      if (activeTicketId) {
        await dispatch(
          getSupportMesaageByTicketIdAction(activeTicketId, token, companyId)
        );
      }
      dispatch(getNotificatonAction(companyId, token));

      setIsLoader(false);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    let newFiles = [];
    let totalSize = attachedFiles.reduce((acc, f) => acc + f.size, 0);
    for (let file of files) {
      const extension = file.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        antdMessage.error(`${file.name} has unsupported file format`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        antdMessage.error(`${file.name} exceeds 10MB limit`);
        continue;
      }
      if (totalSize + file.size > MAX_TOTAL_SIZE) {
        antdMessage.error(`Total attachment size cannot exceed 50MB`);
        break;
      }
      newFiles.push(file);
      totalSize += file.size;
    }
    if (newFiles.length) setAttachedFiles((prev) => [...prev, ...newFiles]);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: 380,
        background:
          "linear-gradient(to bottom, rgba(0,0,0,0.7) 40%, rgba(0,0,0,1) 100%)",
        position: "relative",
      }}
    >
      {isDragging && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 50,
            border: "2px dashed #d4a373",
            borderRadius: "12px",
            background: "rgba(212,163,115,0.08)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "none",
          }}
        >
          <span style={{ fontSize: 40 }}>📎</span>
          <span style={{ color: "#d4a373", fontSize: 15, fontWeight: 600 }}>Drop files to attach</span>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileUpload}
        accept=".docx,.pdf,.png,.jpg,.jpeg,.mp4,.mov,.avi,.mkv,.xlsx,.csv"
        multiple
      />
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid #5d5a58",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <ArrowLeftCircleIcon
            style={{
              color: "#d4a373",
              cursor: "pointer",
              fontSize: "24px",
            }}
            onClick={() => {
              const companyId =
                tokenInfoList?.info?.company?._id ??
                tokenInfoList?.info?.companyId?._id;
              setView("menu");
              if (companyId && token) {
                dispatch(getAllSupportTicketAction(token, companyId));
                dispatch(getNotificatonAction(companyId, token));
              }
            }}
          />
          <div>
            <div
              style={{
                color: "white",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              {getSupportTicketByIdList?.info?.data?.subject ?? activeCategory}
            </div>
            <div style={{ color: "#A8A8A8", fontSize: "11px" }}>
              {getSupportTicketByIdList?.info?.data?.ticketId ?? ""}
            </div>
          </div>
          <SyncOutlined
            onClick={onRefresh}
            spin={isLoader}
            style={{
              color: "white",
              marginLeft: "0px",
              marginTop: "-17px",
            }}
          />
        </div>
        {getSupportTicketByIdList?.info?.data?.status === "opened" && (
          <Popconfirm
            title="Are you sure you want to close this ticket?"
            onConfirm={() =>
              handleCloseTicket(getSupportTicketByIdList?.info?.data?._id)
            }
            okText="Yes"
            cancelText="No"
            className={css`
              .ant-btn {
                font-size: 9px !important;
              }
            `}
          >
            <Button
              size="small"
              style={{
                backgroundColor: "transparent",
                color: "#fca5a5",
                border: "1px solid #ffa39e",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <CloseTicketIcon /> CLOSE TICKET
            </Button>
          </Popconfirm>
        )}
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          padding: "15px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {Array.isArray(supportMessages) &&
          supportMessages.length === 0 &&
          Array.isArray(showDummyMessage) &&
          showDummyMessage.map((i, index) => {
            const isUser = i?.userId === tokenInfoList?.info?._id;
            return (
              <div
                key={index}
                style={{
                  alignSelf: isUser ? "flex-end" : "flex-start",
                  maxWidth: "70%",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    textAlign: isUser ? "right" : "left",
                    marginBottom: "4px",
                    color: "#A8A8A8",
                  }}
                >
                  {isUser
                    ? dayjs().format("hh:mm A")
                    : `Max AI | System ${new Date(
                        i?.createdAt
                      ).toLocaleTimeString()}`}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  {!isUser && (
                    <div
                      style={{
                        // width: "28px",
                        // height: "24px",
                        borderRadius: "50%",
                        background: "#444",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src="https://catalyst-app-bucket.s3.ap-south-1.amazonaws.com/MaxAI.png"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        alt="AI"
                      />
                    </div>
                  )}
                  <div
                    style={{
                      backgroundColor: isUser
                        ? "#f3a27c"
                        : "rgba(255,255,255,0.05)",
                      border: isUser ? "none" : "1px solid #5d5a58",
                      padding: "8px 16px 8px 16px",
                      borderRadius: isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                      color: isUser ? "#222" : "#ccc",
                      fontSize: "14px",
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: parseMarkdown(i?.messageBody || i?.message),
                      }}
                      style={{ display: "inline-block", wordBreak: "break-word" }}
                    />
                  </div>
                </div>
              </div>
            );
          })}

        {Array.isArray(supportMessages) &&
          supportMessages.map((i, index) => {
            const isUser = i?.userId?._id === tokenInfoList?.info?._id;

            const currentMessageDate = dayjs(i?.createdAt).startOf("day");
            const previousMessageDate =
              index > 0
                ? dayjs(supportMessages[index - 1]?.createdAt).startOf("day")
                : null;
            const showDateSeparator =
              !previousMessageDate ||
              !currentMessageDate.isSame(previousMessageDate);

            let displayDate = "";
            if (showDateSeparator) {
              const today = dayjs().startOf("day");
              const yesterday = dayjs().subtract(1, "day").startOf("day");
              if (currentMessageDate.isSame(today)) displayDate = "Today";
              else if (currentMessageDate.isSame(yesterday))
                displayDate = "Yesterday";
              else displayDate = currentMessageDate.format("DD/MM/YYYY");
            }

            return (
              <React.Fragment key={index}>
                {showDateSeparator && (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#A8A8A8",
                      fontSize: "12px",
                      margin: "1px 0",
                    }}
                  >
                    {displayDate}
                  </div>
                )}
                <div
                  style={{
                    alignSelf: isUser ? "flex-end" : "flex-start",
                    maxWidth: "80%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      textAlign: isUser ? "right" : "left",
                      marginBottom: "4px",
                      color: "#A8A8A8",
                    }}
                  >
                    {isUser
                      ? dayjs(i?.createdAt).format("hh:mm A")
                      : !isUser && i?.maxAI
                      ? `Max AI | System ${dayjs(i?.createdAt).format(
                          "hh:mm A"
                        )}`
                      : !isUser && !i?.maxAI
                      ? `${
                          i?.userId?.displayName ||
                          i?.userId?.name?.firstName ||
                          ""
                        } ${dayjs(i?.createdAt).format("hh:mm A")}`
                      : ""}
                  </div>
                  <div style={{ display: "flex" }}>
                    <div style={{ width: "24px" }}>
                      {!isUser && i?.maxAI && (
                        <Avatar
                          src="https://catalyst-app-bucket.s3.ap-south-1.amazonaws.com/MaxAI.png"
                          size={24}
                        />
                      )}
                      {/*<Avatar
                        src={<ProfileCircleIcon style={{ fontSize: "14px" }} />}
                        style={{ background: "#260B3B" }}
                        size={24}
                      />*/}
                      {!isUser && !i?.maxAI && (
                        <Avatar
                          src="https://catalyst-app-bucket.s3.ap-south-1.amazonaws.com/User2.png"
                          size={24}
                        />
                      )}
                    </div>
                    {isUser && i?.files?.length > 0 ? (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                          }}
                        >
                          {i?.files.map((a, idx) => {
                            const fileType = getFileType(a?.url);
                            const tagStyle = getFileTagStyle(fileType);
                            return (
                              <div key={idx} style={tagStyle}>
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "inherit",
                                    fontSize: "14px",
                                  }}
                                >
                                  {getFileIcon(fileType)}
                                </span>
                                <div
                                  style={{ color: "inherit", fontSize: "12px" }}
                                >
                                  {getShortFileNameWithExtension(a?.url, 18)}
                                </div>
                                <DownloadSquareIcon
                                  size="small"
                                  onClick={() => downloadBlobFile(a?.url)}
                                />
                              </div>
                            );
                          })}
                        </div>
                        {(i?.messageBody || i?.message) && (
                          <div
                            style={{
                              backgroundColor: isUser
                                ? "#f3a27c"
                                : "rgba(255,255,255,0.05)",
                              border: isUser ? "none" : "1px solid #5d5a58",
                              padding: "8px 12px",
                              borderRadius: isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                              color: isUser ? "#222" : "#fff",
                              fontSize: "14px",
                              marginTop: "10px",
                            }}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: parseMarkdown(i?.messageBody || i?.message),
                              }}
                              style={{ display: "inline-block", wordBreak: "break-word" }}
                            />
                          </div>
                        )}
                      </div>
                    ) : !isUser && i?.files?.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          marginLeft: "5px",
                        }}
                      >
                        {i?.files.map((a, idx) => {
                          const fileType = getFileType(a?.url);
                          const tagStyle = getFileTagStyle(fileType);
                          return (
                            <div key={idx} style={tagStyle}>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "inherit",
                                  fontSize: "14px",
                                }}
                              >
                                {getFileIcon(fileType)}
                              </span>
                              <div
                                style={{ color: "inherit", fontSize: "12px" }}
                              >
                                {getShortFileNameWithExtension(a?.url, 18)}
                              </div>
                              <DownloadSquareIcon
                                size="small"
                                onClick={() => downloadBlobFile(a?.url)}
                              />
                            </div>
                          );
                        })}
                        {(i?.messageBody || i?.message) && (
                          isOnlyEmojis(i?.messageBody || i?.message)
                            ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: parseMarkdown(i?.messageBody || i?.message),
                                }}
                                style={{ fontSize: 48, lineHeight: 1.2, marginTop: "6px" }}
                              />
                            ) : (
                              <div
                                style={{
                                  backgroundColor: isUser
                                    ? "#f3a27c"
                                    : "rgba(255,255,255,0.05)",
                                  border: isUser ? "none" : "1px solid #5d5a58",
                                  padding: "8px 12px",
                                  borderRadius: isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                                  color: isUser ? "#222" : "#fff",
                                  fontSize: "14px",
                                  marginTop: "10px",
                                }}
                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: parseMarkdown(i?.messageBody || i?.message),
                                  }}
                                  style={{ display: "inline-block", wordBreak: "break-word" }}
                                />
                              </div>
                            )
                        )}
                      </div>
                    ) : (
                      isOnlyEmojis(i?.messageBody || i?.message)
                        ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: parseMarkdown(i?.messageBody || i?.message),
                            }}
                            style={{ fontSize: 48, lineHeight: 1.2, marginLeft: "5px" }}
                          />
                        ) : (
                          <div
                            style={{
                              backgroundColor: isUser
                                ? "#f3a27c"
                                : "rgba(255,255,255,0.05)",
                              border: isUser ? "none" : "1px solid #5d5a58",
                              padding: "8px 12px",
                              borderRadius: isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                              color: isUser ? "#222" : "#fff",
                              fontSize: "14px",
                              marginLeft: "5px",
                            }}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: parseMarkdown(i?.messageBody || i?.message),
                              }}
                              style={{ display: "inline-block", wordBreak: "break-word" }}
                            />
                          </div>
                        )
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

        {isAiResponding && (
          <div
            style={{
              alignSelf: "flex-start",
              display: "flex",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "#444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoadingOutlined style={{ color: "#d4a373", fontSize: "12px" }} />
            </div>
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid #5d5a58",
                padding: "12px 16px",
                borderRadius: "4px 16px 16px 16px",
                color: "#d4a373",
              }}
            >
              Typing...
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          padding: "16px",
          borderTop: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {attachedFiles.length > 0 && (
          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {attachedFiles.map((file, index) => (
              <AttachmentTag
                key={index}
                file={file}
                onRemove={removeFile}
                index={index}
              />
            ))}
          </div>
        )}
        {getSupportTicketByIdList?.info?.data?.status === "opened" ? (
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "8px",
              padding: "8px 12px",
              border: "1px solid #222",
              gap: 10,
            }}
          >
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <ReactQuill
                ref={textAreaRef}
                theme="bubble"
                value={message}
                onChange={setMessage}
                placeholder="Write a message"
                readOnly={isAiResponding || isLoading}
                modules={modules}
                style={{
                  background: "transparent",
                  color: "#fff",
                  width: "100%",
                }}
              />
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {/* Attachment */}
                <Tooltip title="Attach files">
                  <PaperClipOutlined
                    onClick={() => fileInputRef.current?.click()}
                    style={{ color: "#d4a373", fontSize: 15, cursor: "pointer" }}
                  />
                </Tooltip>
                {/* Emoji */}
                <Popover
                  content={
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {["😀", "😂", "😍", "👍", "🔥", "🎉", "❤️", "🤔", "👀", "🚀", "👏", "🙌", "💡", "❌", "✅", "⚠️"].map((emoji) => (
                        <span
                          key={emoji}
                          onClick={() => insertEmoji(emoji)}
                          style={{
                            fontSize: 18,
                            cursor: "pointer",
                            padding: "4px",
                            textAlign: "center",
                            borderRadius: "4px",
                            userSelect: "none",
                          }}
                          onMouseEnter={(e) => e.target.style.background = "rgba(255,255,255,0.15)"}
                          onMouseLeave={(e) => e.target.style.background = "transparent"}
                        >
                          {emoji}
                        </span>
                      ))}
                    </div>
                  }
                  trigger="click"
                  placement="top"
                >
                  <Tooltip title="Emojis">
                    <SmileOutlined style={{ color: "#d4a373", fontSize: 15, cursor: "pointer" }} />
                  </Tooltip>
                </Popover>
                {/* Divider */}
                <div style={{ width: 1, height: 16, background: "#444", margin: "0 2px" }} />
                {/* Formatting */}
                <Tooltip title="Bold (Ctrl+B)">
                  <Button
                    type="text"
                    size="small"
                    icon={<BoldOutlined style={{ fontSize: 13, color: "#d4a373" }} />}
                    onClick={() => insertFormat("bold")}
                    style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center" }}
                  />
                </Tooltip>
                <Tooltip title="Italic (Ctrl+I)">
                  <Button
                    type="text"
                    size="small"
                    icon={<ItalicOutlined style={{ fontSize: 13, color: "#d4a373" }} />}
                    onClick={() => insertFormat("italic")}
                    style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center" }}
                  />
                </Tooltip>
                <Tooltip title="Bulleted List">
                  <Button
                    type="text"
                    size="small"
                    icon={<UnorderedListOutlined style={{ fontSize: 13, color: "#d4a373" }} />}
                    onClick={() => insertFormat("bullet")}
                    style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center" }}
                  />
                </Tooltip>
                <Tooltip title="Numbered List">
                  <Button
                    type="text"
                    size="small"
                    icon={<OrderedListOutlined style={{ fontSize: 13, color: "#d4a373" }} />}
                    onClick={() => insertFormat("number")}
                    style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center" }}
                  />
                </Tooltip>
                <Tooltip title="New Line (Shift+Enter)">
                  <Button
                    type="text"
                    size="small"
                    icon={<EnterOutlined style={{ fontSize: 13, color: "#d4a373" }} />}
                    onClick={() => insertFormat("newline")}
                    style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center" }}
                  />
                </Tooltip>
              </div>
            </div>
            {isAiResponding || isLoading ? (
              <LoadingOutlined style={{ color: "#d4a373", paddingBottom: 8 }} />
            ) : (
              <SendOutlined
                onClick={handleSendMessage}
                style={{
                  color:
                    message.trim() || attachedFiles?.length > 0
                      ? "#d4a373"
                      : "#555",
                  fontSize: "18px",
                  cursor: "pointer",
                  paddingBottom: 8,
                }}
              />
            )}
          </div>
        ) : getSupportTicketByIdList?.info?.data?.status === "closed" ? (
          <div
            style={{
              border: "1px solid #FFFFFF29",
              padding: "16px",
              color: "#A8A8A8",
              textAlign: "center",
            }}
          >
            The ticket was closed
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "8px",
              padding: "8px 12px",
              border: "1px solid #222",
            }}
          >
            <PaperClipOutlined
              style={{
                color: "#d4a373",
                fontSize: "18px",
                cursor: "pointer",
              }}
            />
            <ReactQuill
              placeholder="Write a message"
              value={message}
              readOnly={true}
              theme="bubble"
              style={{
                background: "transparent",
                color: "#8c8c8c",
                width: "100%",
              }}
            />
            <SendOutlined
              style={{
                color: "#555",
                fontSize: "18px",
                cursor: "pointer",
              }}
            />
          </div>
        )}
      <style>{`
        .ql-container.ql-bubble {
          border: none !important;
          font-family: inherit;
          font-size: 14px;
        }
        .ql-editor, .ql-editor * {
          color: #fff !important;
        }
        .ql-editor {
          padding: 4px 0 !important;
          min-height: 40px;
          max-height: 120px;
          overflow-y: auto;
        }
        .ql-editor.ql-blank::before {
          left: 0 !important;
          color: #8c8c8c !important;
          font-style: normal !important;
        }
      `}</style>
    </div>
    </div>
  );
};

export default ChatView;
